import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { chatService } from '../../../services/chatService';
import { boothService } from '../../../services/boothService';
import { useAuth } from '../../../contexts/AuthContext';

const InstituteCard = ({ id, name, logo, banner, location, categories, coursesOffered, isSaved, onToggleSave, instituteUserId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [startingChat, setStartingChat] = useState(false);
  const [applicationLinks, setApplicationLinks] = useState([]);
  const [showApplyModal, setShowApplyModal] = useState(false);

  useEffect(() => {
    const fetchApplicationLinks = async () => {
      if (id) {
        try {
          const boothData = await boothService?.getBoothByInstituteId(id);
          if (boothData?.application_links && Array.isArray(boothData?.application_links)) {
            setApplicationLinks(boothData?.application_links);
          }
        } catch (error) {
          console.error('Error fetching application links:', error);
        }
      }
    };
    
    fetchApplicationLinks();
  }, [id]);

  const handleStartChat = async () => {
    if (!user?.id) {
      alert('Please log in to start a conversation');
      return;
    }

    if (!instituteUserId) {
      // Navigate to explore institutions page where real data is available
      navigate('/explore-institutions');
      return;
    }

    try {
      setStartingChat(true);
      await chatService?.createConversation(user?.id, instituteUserId);
      navigate('/chat-interface');
    } catch (err) {
      console.error('Failed to start conversation:', err);
      alert('Failed to start conversation. Please try again.');
    } finally {
      setStartingChat(false);
    }
  };

  const handleApplyNow = () => {
    if (!user) {
      alert('Please login to apply');
      navigate('/student-registration');
      return;
    }

    if (applicationLinks?.length === 0) {
      alert('Application link not available. Please visit the booth for more information.');
      return;
    }

    if (applicationLinks?.length === 1) {
      // Single link - redirect directly
      window.open(applicationLinks?.[0]?.url, '_blank');
    } else {
      // Multiple links - show modal
      setShowApplyModal(true);
    }
  };

  const handleVisitBooth = () => {
    navigate(`/virtual-booth/${id}`);
  };

  return (
    <>
      <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-smooth group">
        <div className="relative h-40">
          <div className="w-full h-full overflow-hidden">
            <Image
              src={banner || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1'}
              alt={`${name} campus view`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
          <button
            onClick={onToggleSave}
            className="absolute top-3 right-3 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-smooth z-10"
          >
            <Icon
              name={isSaved ? 'Heart' : 'Heart'}
              size={20}
              color={isSaved ? 'var(--color-error)' : 'var(--color-foreground)'}
              fill={isSaved ? 'var(--color-error)' : 'none'}
            />
          </button>
          {/* Logo positioned overlapping bottom of banner - outside overflow-hidden so it's fully visible */}
          <div className="absolute -bottom-8 left-6 z-20">
            <div className="w-16 h-16 bg-white rounded-lg border-2 border-white shadow-lg flex items-center justify-center overflow-hidden">
              {logo ? (
                <Image
                  src={logo}
                  alt={`${name} logo`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Icon name="Building2" size={32} color="var(--color-primary)" />
              )}
            </div>
          </div>
        </div>
        <div className="p-6 pt-10">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-foreground mb-1 truncate">
                {name}
              </h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="MapPin" size={14} />
                <span className="truncate">{location}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {categories?.slice(0, 3)?.map((category, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
              >
                {category}
              </span>
            ))}
            {categories?.length > 3 && (
              <span className="px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full">
                +{categories?.length - 3} more
              </span>
            )}
          </div>

          <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{coursesOffered}</p>
              <p className="text-xs text-muted-foreground">Courses</p>
            </div>
          </div>

          <div className="space-y-2">
            {applicationLinks?.length > 0 && (
              <Button
                variant="default"
                size="sm"
                fullWidth
                iconName="ExternalLink"
                iconPosition="left"
                onClick={handleApplyNow}
                className="bg-accent hover:bg-accent/90"
              >
                Apply Now
              </Button>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="MessageSquare"
                iconPosition="left"
                onClick={handleStartChat}
                disabled={startingChat}
              >
                {startingChat ? 'Starting...' : 'Chat'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="Building2"
                onClick={handleVisitBooth}
              >
                Visit Booth
              </Button>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="Compass"
                onClick={() => navigate(`/360-campus-tour/${id}`)}
              >
                360° Campus Tour
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Apply Now Modal - Multiple Links */}
      {showApplyModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-1020" onClick={() => setShowApplyModal(false)} />
          <div className="fixed inset-0 z-1030 flex items-center justify-center p-4">
            <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border">
                <h3 className="text-lg font-heading font-semibold text-foreground">Application Links</h3>
                <button onClick={() => setShowApplyModal(false)} className="p-1 hover:bg-muted rounded-lg transition-smooth">
                  <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-3">
                {applicationLinks?.map((link, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      window.open(link?.url, '_blank');
                      setShowApplyModal(false);
                    }}
                    className="w-full flex items-center justify-between p-3 bg-muted hover:bg-muted/80 rounded-lg transition-smooth group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="ExternalLink" size={18} color="var(--color-primary)" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-foreground">{link?.title || `Application Form ${index + 1}`}</p>
                        <p className="text-xs text-muted-foreground">{link?.description || 'Click to open'}</p>
                      </div>
                    </div>
                    <Icon name="ChevronRight" size={18} color="var(--color-muted-foreground)" className="group-hover:translate-x-1 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default InstituteCard;